// Client Comms utility types and helpers

export type CommsKind =
  | 'card_created'
  | 'comment'
  | 'description_changed'
  | 'card_moved'
  | 'card_renamed'
  | 'attachment_added'
  | 'manual_import'
  | 'email';
export type CommsStatus = 'open' | 'cleared';

export interface CommsSyncState {
  id: boolean;
  last_run_at: string | null;
  last_success_at: string | null;
  status: 'ok' | 'error';
  error_detail: string | null;
  boards_synced: number | null;
  new_items: number | null;
  updated_at: string;
}

export type CommsBoard = {
  id: string;
  trello_board_id: string;
  board_name: string;
  client_id: string | null;
  is_active: boolean;
  last_synced_action_at: string | null;
};

export type CommsItem = {
  id: string;
  trello_action_id: string;
  board_id: string;
  card_id: string;
  card_name: string;
  card_url: string;
  card_list_name: string | null;
  kind: CommsKind;
  actor_name: string;
  actor_trello_id: string;
  body: string | null;
  occurred_at: string;
  status: CommsStatus;
  cleared_by: string | null;
  cleared_at: string | null;
  cleared_via: 'manual' | 'trello_reply' | 'card_archived' | 'task_submitted' | null;
  direction: 'inbound' | 'outbound';
  /** 'trello' for synced card activity, 'email' for an escalated message. */
  source?: 'trello' | 'email';
  /** Set on email rows only; Trello rows resolve their client via the board. */
  client_id?: string | null;
  created_at: string;
  comms_boards?: { board_name: string; client_id: string | null } | null;
};

// The task governing a comms card, via comms_task_links (a card belongs to
// at most one task; a task may hold many cards)
export interface CardTaskInfo {
  id: string;
  status: string | null;
  archived: boolean;
  assigned_to_name: string | null;
  due_date: string | null;
}

// Which tab a card belongs to. 'assigned' is DERIVED from the linked task:
// an active task (not archived, not Submitted) owns the card. Items stay
// 'open' underneath, so archiving a task without submitting drops the card
// straight back to Open; submitting clears the items via a DB trigger.
export type CommsView = 'open' | 'assigned' | 'cleared';

export function isTaskActive(task: CardTaskInfo | null | undefined): boolean {
  return !!task && !task.archived && task.status !== 'Submitted';
}

// One row on the Client Comms screen = one card with activity. The index
// signature keeps the group compatible with the generic Table component.
export interface CardGroup {
  cardId: string;
  cardName: string;
  cardUrl: string;
  boardName: string;
  /**
   * An escalated email rather than Trello card activity. It has no board, no
   * list and no live Trello state, so the panel must not offer any of that.
   */
  isEmail: boolean;
  boardClientId: string | null;
  /**
   * The client this card belongs to, whichever way it got one: a Trello card
   * through its board, an email through the client a human attached to it.
   * Everything downstream (convert-to-task, task lookup) uses this.
   */
  clientId: string | null;
  listName: string | null;
  items: CommsItem[];
  latest: CommsItem;
  openCount: number;
  counts: Record<CommsKind, number>;
  task: CardTaskInfo | null;
  [key: string]: unknown;
}

export const KIND_LABELS: Record<CommsKind, string> = {
  card_created: 'New card',
  comment: 'Comment',
  description_changed: 'Desc updated',
  card_moved: 'Moved',
  card_renamed: 'Renamed',
  attachment_added: 'Attachment',
  manual_import: 'Imported',
  email: 'Email',
};

export const zeroKindCounts = (): Record<CommsKind, number> => ({
  card_created: 0,
  comment: 0,
  description_changed: 0,
  card_moved: 0,
  card_renamed: 0,
  attachment_added: 0,
  manual_import: 0,
  email: 0,
});

// Group items by card. Only INBOUND (client) items decide whether a card
// surfaces and drive its badges/excerpt; outbound team replies ride along in
// `items` so the conversation panel shows both sides of the thread. The tab
// a card lands in: active linked task -> 'assigned'; else open items ->
// 'open'; else 'cleared'.
export function groupItemsByCard(
  items: CommsItem[],
  tasksByCard: Map<string, CardTaskInfo>,
  view: CommsView
): CardGroup[] {
  const byCard = new Map<string, CommsItem[]>();
  items.forEach(item => {
    const existing = byCard.get(item.card_id) || [];
    existing.push(item);
    byCard.set(item.card_id, existing);
  });

  const groups: CardGroup[] = [];
  byCard.forEach((cardItems, cardId) => {
    const inbound = cardItems.filter(i => i.direction === 'inbound');
    if (inbound.length === 0) return; // only team replies — nothing to surface

    const task = tasksByCard.get(cardId) ?? null;
    const hasOpen = inbound.some(i => i.status === 'open');
    const state: CommsView = isTaskActive(task) ? 'assigned' : hasOpen ? 'open' : 'cleared';
    if (state !== view) return;

    const openInbound = inbound.filter(i => i.status === 'open');
    // Cleared view summarizes all inbound; open/assigned summarize what's
    // open (fall back to all inbound for an assigned card with nothing open)
    const active = view === 'cleared' || openInbound.length === 0 ? inbound : openInbound;
    const sorted = [...active].sort(
      (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
    );
    const latest = sorted[0];

    const counts = zeroKindCounts();
    active.forEach(i => { counts[i.kind]++; });

    groups.push({
      cardId: latest.card_id,
      cardName: latest.card_name,
      cardUrl: latest.card_url,
      // card_id is the belt to source's braces: an email row is 'email:<thread>'
      // whether or not the column made it into the select.
      isEmail: latest.source === 'email' || String(latest.card_id).startsWith('email:'),
      boardName: latest.comms_boards?.board_name || '—',
      boardClientId: latest.comms_boards?.client_id ?? null,
      clientId:
        latest.source === 'email' || String(latest.card_id).startsWith('email:')
          ? latest.client_id ?? null
          : latest.comms_boards?.client_id ?? null,
      listName: latest.card_list_name,
      // Newest first — the conversation panel reads top-down like an inbox
      items: [...cardItems].sort(
        (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      ),
      latest,
      openCount: openInbound.length,
      counts,
      task,
    });
  });

  // Open/Assigned tabs are work queues: most stale first (longest-waiting
  // client at the top). Cleared tab is history: most recent first.
  return groups.sort((a, b) => {
    const delta = new Date(b.latest.occurred_at).getTime() - new Date(a.latest.occurred_at).getTime();
    return view === 'cleared' ? delta : -delta;
  });
}

// Compact age: minutes to 60, hours to 24, days to 7, then weeks, months, years
export function age(iso: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
}

export function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function excerpt(text: string | null, max = 140): string {
  if (!text) return '';
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}
